/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IHelp, IOrderSummary, IPaymentMethods } from '@msdyn365-commerce-modules/order-management';
import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { ICheckoutOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management';
import { OrderConfirmationDetail } from './components/OrderConfirmationDetail';
import { ICheckoutOrderConfirmationProps } from '../definition-extensions/checkout-order-confirmation.ext.props.autogenerated';
import { ICheckoutOrderConfirmationData } from '../definition-extensions/checkout-order-confirmation.data';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { InvoicePaymentConfirmationDetail } from './components/InvoicePaymentConfirmationDetail';

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps
    // receiptEmail
    // createdDate,
    // channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {/* {channelReferenceId} */}
        {/* {receiptEmail} */}
    </Node>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    pickupDateTimeslot,
    trackingInfo
}) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {address}
        {pickupDateTimeslot}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderConfirmationGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<ICheckoutOrderConfirmationViewProps &
    ICheckoutOrderConfirmationProps<ICheckoutOrderConfirmationData>> = ({
    moduleProps,
    heading,
    backToShoppingLink,
    alert,
    loading,
    orderSummary,
    payment,
    help,
    groups,
    resources,
    ...rest
}) => {
    const DLV_MODE_KEY = 'dlv-mode';
    const [dlvMode, setDlvMode] = React.useState<string>('');
    const salesOrder = rest.data.checkout.result?.salesOrder;
    const cart = rest.data.checkout.result?.checkoutCart.cart;

    const orderedProducts = rest.data.checkout.result?.orderedProducts || [];
    React.useEffect(() => {
        setDlvMode(window.sessionStorage.getItem(DLV_MODE_KEY) || '');
        sessionStorage.removeItem(DLV_MODE_KEY);
    }, []);

    return (
        <Module {...moduleProps}>
            {/*{heading}*/}
            {alert}
            {loading}
            {salesOrder &&
                (rest.salesOrder?.SalesLines?.[0]?.IsInvoiceLine ? (
                    <InvoicePaymentConfirmationDetail
                        cultureFormatter={rest.context.cultureFormatter}
                        salesOrder={salesOrder}
                        customer={rest.data.customerInformation.result}
                        deliveryMode={dlvMode}
                        orderedProducts={orderedProducts as SimpleProduct[]}
                        resources={resources}
                        chargeLinesBreakdown={rest.context.app.config.chargeLinesBreakdown}
                        cartLines={cart?.CartLines || []}
                    />
                ) : (
                    <OrderConfirmationDetail
                        cultureFormatter={rest.context.cultureFormatter}
                        salesOrder={salesOrder}
                        customer={rest.data.customerInformation.result}
                        deliveryMode={dlvMode}
                        orderedProducts={orderedProducts as SimpleProduct[]}
                        resources={resources}
                        chargeLinesBreakdown={rest.context.app.config.chargeLinesBreakdown}
                    />
                ))}
            {/* {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} />} */}
            {backToShoppingLink}
            {/*{groups && <OrderConfirmationGroups {...groups} />}*/}
            {/*{orderSummary && <OrderConfirmationOrderSummary {...orderSummary} />}*/}
            {payment && <OrderConfirmationPayment {...payment} />}
            {help && <OrderConfirmationHelp {...help} />}
        </Module>
    );
};

export default OrderConfirmationView;
